import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Row, Col, Statistic, message, Space, Tabs, Badge, Progress, TimePicker, DatePicker, Alert } from 'antd';
import { PlusOutlined, ClockCircleOutlined, CheckCircleOutlined, LoginOutlined, LogoutOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from 'services/api/apiService';

const statusColors: Record<string,string> = { present:'success', absent:'error', late:'warning', half_day:'processing', on_leave:'default', holiday:'cyan', weekend:'default' };

const AttendanceManagement: React.FC = () => {
  const [attendance, setAttendance] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('attendance');
  const [period, setPeriod] = useState(dayjs().format('YYYY-MM'));
  const [clockInModal, setClockInModal] = useState(false);
  const [manualModal, setManualModal] = useState(false);
  const [shiftModal, setShiftModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [clockInForm] = Form.useForm();
  const [manualForm] = Form.useForm();
  const [shiftForm] = Form.useForm();
  const [assignForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, [period, activeTab]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [er, ar, sr, smr] = await Promise.all([
        apiService.getEmployees(),
        apiService.getAttendance(period),
        apiService.getShifts(),
        apiService.getAttendanceSummary(period),
      ]);
      if (er.success) setEmployees(er.data || []);
      if (ar.success) setAttendance(ar.data || []);
      if (sr.success) setShifts(sr.data || []);
      if (smr.success) setSummary(smr.data);
    } finally { setLoading(false); }
  };

  const handleClockIn = async (values: any) => {
    let coords: { latitude: number; longitude: number } | undefined = undefined;
    let method = 'web';
    try {
      const pos: any = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) { reject(new Error('no geo')); return; }
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, enableHighAccuracy: true });
      });
      coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
      method = 'geo';
    } catch (e) { /* location unavailable - fall back to standard web clock-in */ }
    const r = await apiService.clockIn(values.employee_id, method, coords);
    if (r.success) {
      message.success(`${r.action === 'clock_in' ? '✅ Clocked In' : '🏁 Clocked Out'} at ${r.time}${method === 'geo' ? ' 📍' : ''}${r.late_minutes > 0 ? ` (${r.late_minutes} mins late)` : ''}`);
      setClockInModal(false); clockInForm.resetFields(); fetchAll();
    } else message.error(r.error || 'Failed');
  };

  const handleManual = async (values: any) => {
    const r = await apiService.recordAttendance({
      ...values,
      clock_in: values.clock_in?.format('HH:mm:ss'),
      clock_out: values.clock_out?.format('HH:mm:ss'),
    });
    if (r.success) { message.success('Attendance recorded'); setManualModal(false); manualForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleShift = async (values: any) => {
    const r = await apiService.createShift(values);
    if (r.success) { message.success('Shift created'); setShiftModal(false); shiftForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleAssign = async (values: any) => {
    const r = await apiService.assignShift(values.employee_id, values.shift_id);
    if (r.success) { message.success('Shift assigned'); setAssignModal(false); assignForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const empOptions = employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>);

  const attendanceTab = (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}><Card><Statistic title="Total Records" value={summary?.total_records || 0} /></Card></Col>
        <Col span={4}><Card><Statistic title="Present" value={summary?.present || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Absent" value={summary?.absent || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Late" value={summary?.late || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Avg Hours" value={summary?.avg_hours || 0} suffix="hrs" precision={1} /></Card></Col>
        <Col span={4}><Card><Statistic title="Total Overtime" value={summary?.total_overtime || 0} suffix="hrs" precision={1} valueStyle={{ color: '#1890ff' }} /></Card></Col>
      </Row>
      <Card title="Attendance Records" extra={
        <Space>
          <DatePicker picker="month" value={dayjs(period)} onChange={d => setPeriod(d ? d.format('YYYY-MM') : dayjs().format('YYYY-MM'))} />
          <Button icon={<LoginOutlined />} onClick={() => setClockInModal(true)}>Clock In/Out</Button>
          <Button icon={<PlusOutlined />} onClick={() => setManualModal(true)}>Manual Entry</Button>
        </Space>
      }>
        <Table dataSource={attendance} rowKey="id" loading={loading} pagination={{ pageSize: 15 }}
          columns={[
            { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => <><div>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.department}</div></> },
            { title: 'Date', dataIndex: 'attendance_date' },
            { title: 'Clock In', dataIndex: 'clock_in', render: (v: string) => v || '-' },
            { title: 'Clock Out', dataIndex: 'clock_out', render: (v: string) => v || '-' },
            { title: 'Hours', dataIndex: 'hours_worked', render: (v: number) => v ? `${v}h` : '-' },
            { title: 'Overtime', dataIndex: 'overtime_hours', render: (v: number) => v > 0 ? <Tag color="blue">{v}h OT</Tag> : '-' },
            { title: 'Late (mins)', dataIndex: 'late_minutes', render: (v: number) => v > 0 ? <Tag color="warning">{v} mins</Tag> : '-' },
            { title: 'Status', dataIndex: 'status', render: (v: string) => <Badge status={statusColors[v] as any} text={v} /> },
          ]} />
      </Card>
    </div>
  );

  const shiftsTab = (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {shifts.map((s: any) => (
          <Col span={6} key={s.id}>
            <Card size="small" title={s.shift_name} extra={<Tag color={s.shift_type === 'morning' ? 'blue' : s.shift_type === 'night' ? 'purple' : 'orange'}>{s.shift_type}</Tag>}>
              <p style={{ margin: 0 }}>🕐 {s.start_time} — {s.end_time}</p>
              <p style={{ margin: '4px 0', fontSize: 12, color: '#666' }}>☕ {s.break_minutes} min break • {s.working_hours}h/day</p>
              <p style={{ margin: 0, fontSize: 12, color: '#999' }}>👥 {s.assigned_employees} employees</p>
            </Card>
          </Col>
        ))}
      </Row>
      <Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setShiftModal(true)}>Create Shift</Button>
        <Button icon={<PlusOutlined />} onClick={() => setAssignModal(true)}>Assign Shift</Button>
      </Space>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <h1>Attendance & Shift Management</h1>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        { key: 'attendance', label: '📅 Attendance', children: attendanceTab },
        { key: 'shifts', label: '🕐 Shift Management', children: shiftsTab },
      ]} />

      {/* Clock In/Out Modal */}
      <Modal title="Clock In / Clock Out" open={clockInModal} onCancel={() => setClockInModal(false)} footer={null} width={400}>
        <Alert message="If already clocked in, this will clock you out and compute hours worked." type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={clockInForm} layout="vertical" onFinish={handleClockIn}>
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item>
          <Space><Button type="primary" htmlType="submit" icon={<ClockCircleOutlined />}>Clock In / Clock Out</Button><Button onClick={() => setClockInModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Manual Entry Modal */}
      <Modal title="Manual Attendance Entry" open={manualModal} onCancel={() => setManualModal(false)} footer={null} width={560}>
        <Form form={manualForm} layout="vertical" onFinish={handleManual}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="attendance_date" label="Date" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="clock_in" label="Clock In Time"><TimePicker format="HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="clock_out" label="Clock Out Time"><TimePicker format="HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="hours_worked" label="Hours Worked"><Input type="number" step="0.5" /></Form.Item></Col>
            <Col span={12}><Form.Item name="overtime_hours" label="Overtime Hours"><Input type="number" step="0.5" /></Form.Item></Col>
          </Row>
          <Form.Item name="status" label="Status" initialValue="present">
            <Select>{Object.keys(statusColors).map(s => <Select.Option key={s} value={s}>{s.replace(/_/g,' ')}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes"><Input /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Save</Button><Button onClick={() => setManualModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Create Shift Modal */}
      <Modal title="Create Shift" open={shiftModal} onCancel={() => setShiftModal(false)} footer={null} width={500}>
        <Form form={shiftForm} layout="vertical" onFinish={handleShift}>
          <Form.Item name="shift_name" label="Shift Name" rules={[{ required: true }]}><Input placeholder="e.g. Morning Shift" /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}><Input type="time" /></Form.Item></Col>
            <Col span={12}><Form.Item name="end_time" label="End Time" rules={[{ required: true }]}><Input type="time" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="break_minutes" label="Break (minutes)" initialValue={60}><Input type="number" /></Form.Item></Col>
            <Col span={12}><Form.Item name="shift_type" label="Shift Type" initialValue="morning">
              <Select><Select.Option value="morning">Morning</Select.Option><Select.Option value="afternoon">Afternoon</Select.Option><Select.Option value="night">Night</Select.Option><Select.Option value="flexible">Flexible</Select.Option></Select>
            </Form.Item></Col>
          </Row>
          <Space><Button type="primary" htmlType="submit">Create</Button><Button onClick={() => setShiftModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Assign Shift Modal */}
      <Modal title="Assign Shift to Employee" open={assignModal} onCancel={() => setAssignModal(false)} footer={null} width={450}>
        <Form form={assignForm} layout="vertical" onFinish={handleAssign}>
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{empOptions}</Select></Form.Item>
          <Form.Item name="shift_id" label="Shift" rules={[{ required: true }]}>
            <Select>{shifts.map((s: any) => <Select.Option key={s.id} value={s.id}>{s.shift_name} ({s.start_time}—{s.end_time})</Select.Option>)}</Select>
          </Form.Item>
          <Space><Button type="primary" htmlType="submit">Assign</Button><Button onClick={() => setAssignModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};
export default AttendanceManagement;
